import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { components as ReactSelectComponents } from "react-select";
import type { GroupBase, MenuListProps, OptionProps, StylesConfig } from "react-select";

import CheckIcon from "@assets/icons/check.svg";
import SearchIcon from "@assets/icons/search.svg";

import { useScrollLock } from "@hooks/useScrollLock";
import { Checkbox } from "@ui/FormElements";

import { APP_COLORS } from "@styles/colors";
import useSystemStore from "@store/useSystemStore";
import { cn } from "@utils/tailwind.util";

import { Select } from "./Select";
import type { DefaultSelectOption, SelectProps } from "./Select";

/** The control itself never takes typed input — the search box lives inside the menu. */
type OwnedProps =
  | "isSearchable"
  | "menuIsOpen"
  | "onMenuOpen"
  | "onMenuClose"
  | "hideSelectedOptions";

export interface SearchableSelectProps<OptionType, IsMulti extends boolean = false> extends Omit<
  SelectProps<OptionType, IsMulti>,
  OwnedProps
> {
  /** Placeholder for the in-menu search box. */
  searchPlaceholder?: string;
}

const getOptionLabel = (option: unknown): string =>
  String((option as { label?: unknown })?.label ?? "");

/**
 * A `Select` whose menu opens with a search box on top, single-select options marked with a
 * checkmark and multi-select options with a checkbox — matching the CRM's dropdowns.
 *
 * Only flat option lists are supported (no `GroupBase` groups), which is what the search filter
 * and the option renderer assume.
 */
export function SearchableSelect<
  OptionType = DefaultSelectOption,
  IsMulti extends boolean = false,
>({
  options,
  isMulti,
  onChange,
  styles,
  components: extraComponents,
  searchPlaceholder = "Search",
  ...rest
}: SearchableSelectProps<OptionType, IsMulti>) {
  const { isDarkMode } = useSystemStore();

  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  useScrollLock({ enabled: menuIsOpen, containerRef });

  const closeMenu = useCallback(() => {
    setMenuIsOpen(false);
    setSearch("");
    if (searchInputRef.current) searchInputRef.current.value = "";
  }, []);

  // react-select closes the menu the moment focus leaves its own input — which is exactly what
  // happens when the search box below takes focus. So the menu is driven from here instead, and
  // an outside click is what closes it.
  useEffect(() => {
    if (!menuIsOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node)) return;
      closeMenu();
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuIsOpen, closeMenu]);

  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return options;

    return (options ?? []).filter((option) => getOptionLabel(option).toLowerCase().includes(query));
  }, [options, search]);

  // Stable so the search box isn't remounted (and unfocused) on every keystroke.
  const setSearchInputRef = useCallback((element: HTMLInputElement | null) => {
    searchInputRef.current = element;
    element?.focus();
  }, []);

  const selectComponents = useMemo(
    () => ({
      ...extraComponents,
      Option: (optionProps: OptionProps<OptionType, IsMulti, GroupBase<OptionType>>) => (
        <ReactSelectComponents.Option {...optionProps}>
          <div className="flex w-full items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              {isMulti && (
                <Checkbox
                  variant="small"
                  parentClassName="flex-shrink-0"
                  className="cursor-pointer"
                  isChecked={optionProps.isSelected}
                  readOnly
                />
              )}
              <span className="truncate">{optionProps.label}</span>
            </div>
            {!isMulti && optionProps.isSelected && (
              <CheckIcon width={16} height={16} className="flex-shrink-0 text-teal-600" />
            )}
          </div>
        </ReactSelectComponents.Option>
      ),
      MenuList: ({
        children,
        ...menuListProps
      }: MenuListProps<OptionType, IsMulti, GroupBase<OptionType>>) => (
        <ReactSelectComponents.MenuList {...menuListProps}>
          <div
            className={cn(
              "sticky top-0 z-10 -mx-1 mb-1 flex h-[38px] items-center gap-1 border-b px-2",
              isDarkMode ? "border-gray-700 bg-gray-850" : "border-gray-200 bg-white"
            )}
          >
            <SearchIcon width={20} height={20} className="flex-shrink-0 text-gray-400" />
            <input
              ref={setSearchInputRef}
              type="text"
              placeholder={searchPlaceholder}
              className={cn(
                "h-5 w-full flex-1 border-none bg-transparent p-0 text-sm shadow-none outline-none ring-0",
                "placeholder:text-gray-400 focus:border-none focus:outline-none focus:ring-0 focus:shadow-none",
                isDarkMode ? "text-white" : "text-content-primary"
              )}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Escape") closeMenu();
                else event.stopPropagation();
              }}
              onMouseDown={(event) => event.stopPropagation()}
            />
          </div>
          {children}
        </ReactSelectComponents.MenuList>
      ),
    }),
    [extraComponents, isMulti, isDarkMode, searchPlaceholder, setSearchInputRef, closeMenu]
  );

  // The base styles leave a selected option looking untouched; the CRM tints it instead.
  const selectedOptionStyles = useMemo(
    () =>
      ({
        option: (provided, { isSelected }) => ({
          ...provided,
          backgroundColor: isSelected
            ? isDarkMode
              ? APP_COLORS.gray["750"]
              : APP_COLORS.teal["50"]
            : provided.backgroundColor,
        }),
      }) as StylesConfig<OptionType, IsMulti>,
    [isDarkMode]
  );

  return (
    <div ref={containerRef} className="w-full">
      <Select<OptionType, IsMulti>
        {...rest}
        isMulti={isMulti}
        isSearchable={false}
        options={filteredOptions}
        hideSelectedOptions={false}
        closeMenuOnSelect={!isMulti}
        menuIsOpen={menuIsOpen}
        onMenuOpen={() => setMenuIsOpen(true)}
        onMenuClose={() => {}}
        components={selectComponents}
        styles={styles ? { ...selectedOptionStyles, ...styles } : selectedOptionStyles}
        onChange={(newValue, actionMeta) => {
          if (!isMulti) closeMenu();
          onChange?.(newValue, actionMeta);
        }}
      />
    </div>
  );
}
